import { useMutation, useQuery } from '@tanstack/react-query'
import { TRPCClientError } from '@trpc/client'
import { Send } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useI18n } from '@/lib/i18n'
import { roomChatRefetchIntervalMs } from '@/lib/room-stream'
import { useTRPC } from '@/lib/trpc'
import { cn } from '@/lib/utils'

const MAX_BODY = 500

export function RoomChat({
  roomId,
  isOpen,
  refreshKey,
  streaming,
}: {
  roomId: string
  isOpen: boolean
  refreshKey: number
  streaming: boolean
}) {
  const trpc = useTRPC()
  const { t } = useI18n()
  const [draft, setDraft] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  const messagesQuery = useQuery({
    ...trpc.roomRouter.messages.queryOptions({ roomId }),
    enabled: !!roomId,
    refetchOnWindowFocus: true,
    refetchInterval: roomChatRefetchIntervalMs({ streaming, open: isOpen }),
  })

  const { mutateAsync: post, isPending } = useMutation(
    trpc.roomRouter.postMessage.mutationOptions(),
  )

  const messages = messagesQuery.data ?? []
  const { refetch } = messagesQuery

  useEffect(() => {
    if (roomId)
      refetch().catch(() => {})
  }, [refreshKey, roomId, refetch])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' })
  }, [messages.length])

  const send = async () => {
    const body = draft.trim()
    if (!body)
      return
    try {
      await post({ roomId, body })
      setDraft('')
      await refetch()
    }
    catch (error) {
      const key = error instanceof TRPCClientError ? error.message : 'room_chat_failed'
      toast.error(t(`room.chat.error.${key}`))
    }
  }

  return (
    <Card className="flex h-full flex-col" data-testid="room-chat">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{t('room.chat.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="min-h-48 flex-1 space-y-2 overflow-y-auto pr-1" data-testid="room-chat-messages">
          {messages.length === 0
            ? <p className="text-xs text-muted-foreground">{t('room.chat.empty')}</p>
            : messages.map(m => (
                <div key={m.id} className={cn('text-sm', m.isMine && 'text-right')}>
                  <span className="mr-2 text-xs text-muted-foreground">{m.authorName}</span>
                  <span className="break-words">{m.body}</span>
                </div>
              ))}
          <div ref={bottomRef} />
        </div>

        {isOpen
          ? (
              <div className="flex gap-2">
                <Input
                  value={draft}
                  maxLength={MAX_BODY}
                  placeholder={t('room.chat.placeholder')}
                  data-testid="room-chat-input"
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      send().catch(() => {})
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={isPending || !draft.trim()}
                  data-testid="room-chat-send"
                  onClick={() => { send().catch(() => {}) }}
                >
                  <Send className="size-4" />
                </Button>
              </div>
            )
          : (
              <p className="text-xs text-muted-foreground" data-testid="room-chat-closed">
                {t('room.chat.closed')}
              </p>
            )}
      </CardContent>
    </Card>
  )
}
